import "./profile-page.less"
import Layout from "../layouts/layout"
import { ErrorAlert } from "../components/alert"
import { useMessages } from "../language/language"
import { Link } from "react-router-dom"
import {
  AddUserToClassError,
  ProfileChange,
  serverProfileGetClasses,
  serverProfileJoinClass,
  serverUpdateProfile,
  serverUploadProfilePicture,
} from "utils/server"
import LoadingLamaContainer from "../components/loading-lama-container"
import FormContainer from "../components/forms/form-container"
import EditableField from "../components/forms/editable-field"
import { ImageUploader } from "../components/image-uploader"
import { ProfilePageState, ProfilePageValidation } from "../play-model"
import FlexContainer from "../components/flex-container"
import TextInputGroup from "../components/forms/text-input-group"
import { serverRequestErrorReducer } from "play/reducers"
import { Fragment, useEffect, useState } from "react"
import { usePlayState } from "play/hooks/use-play-state"
import { titleCase } from "utils/helpers"
import usePartialState from "utils/use-partial-state"
import { asValidationProblemDetailsErrors, LoggedInUser } from "models/dto"
import EmailUndeliverableAlert from "../components/email-undeliverable-alert"

export default function ProfilePage() {
  const msg = useMessages()

  const { loggedUser, serverError, modifyState } = usePlayState()
  const [isLoading, setIsLoading] = useState(false)

  const pageTitle = msg.pageTitleProfile

  useEffect(() => {
    document.title = msg.pageTitle(pageTitle)

    setIsLoading(true)

    serverProfileGetClasses()
      .then((resp) => {
        setIsLoading(false)
        modifyState((state) => {
          state.profilePage.classes = resp
        })
      })
      .catch((e) => {
        setIsLoading(false)
        modifyState((state) => {
          serverRequestErrorReducer(state, e)
        })
      })
  }, [])

  return (
    <Layout
      timerEnabled={false}
      screenCounterEnabled={false}
      scrollingEnabled={true}
      nextEnabled={false}
      prevEnabled={false}
      pointsEnabled={false}
      logoIsLink={true}
      hasFooter={false}
    >
      <div className="profile-page">
        <ErrorAlert error={msg.serverErrorToString(serverError)} />
        <p className="breadcrumb">
          <Link to="/">{msg.back}</Link>
        </p>
        {/*<h1 className="dot">{pageTitle}</h1>*/}

        <LoadingLamaContainer isLoading={loggedUser === null || isLoading}>
          <ProfilePageLoaded user={loggedUser!} />
        </LoadingLamaContainer>
      </div>
    </Layout>
  )
}

function ProfilePageLoaded(props: { user: LoggedInUser }) {
  const { user } = props
  const msg = useMessages()
  const { profilePage, modifyState } = usePlayState()
  const [state, setState, setPartialState] = usePartialState({
    invitationCode: "",
    isInvitationExpanded: false,
    joinError: null as string | null,
    joinLoading: false,
    isLoading: false,
  })

  function handleClearValidation(key: keyof ProfilePageValidation) {
    modifyState((state) => {
      state.profilePage.validationErrors[key] = null
    })
  }

  function handleProfileChanged(change: ProfileChange) {
    setPartialState({ isLoading: true })

    return serverUpdateProfile(change)
      .then(() => {
        setPartialState({ isLoading: false })
        modifyState((state) => {
          if (change.email) {
            state.profilePage.validationErrors.email = null
          }
          if (change.name) {
            state.profilePage.validationErrors.name = null
          }

          if (state.loggedUser) {
            state.loggedUser = {
              ...state.loggedUser,
              ...change,
            }
            if (change.email) {
              // accepted by the server, so the sender can deliver to it
              state.loggedUser.emailUndeliverable = false
            }
          }
        })
      })
      .catch((e) => {
        setPartialState({ isLoading: false })
        modifyState((state) => {
          const profile = state.profilePage

          const serverError = e

          const validationErrors = asValidationProblemDetailsErrors(serverError)

          function setError(field: keyof ProfilePageState["validationErrors"]) {
            const aspNetFieldName = titleCase(field)
            const fieldErrors = validationErrors[aspNetFieldName]

            if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
              profile.validationErrors[field] = fieldErrors.join("\n")
            } else if (change.hasOwnProperty(field)) {
              profile.validationErrors[field] = msg.serverError_generic
            }
          }

          console.log(
            "profile change validation error",
            validationErrors,
            serverError
          )

          setError("name")
          setError("city")
          setError("profilePicture")
          setError("email")
        })
      })
  }

  function handleProfilePictureChanged(file: File) {
    return serverUploadProfilePicture(file)
      .then((resp) => {
        return serverUpdateProfile({ profilePictureUrl: resp.url })
      })
      .then((resp) => {
        modifyState((state) => {
          if (!state.loggedUser) return
          state.loggedUser.profilePictureUrl = resp.profilePictureUrl!
        })
      })
  }

  function handleJoinClassRequested() {
    setState((prev) => ({ ...prev, joinLoading: true, joinError: null }))

    serverProfileJoinClass({ invitationCode: state.invitationCode })
      .then((resp) => {
        let error: string | null = null

        switch (resp.error) {
          case AddUserToClassError.None:
            error = null
            break

          case AddUserToClassError.ClassNotFound:
            error = msg.addUserToClassError_classNotFound
            break

          case AddUserToClassError.InvitationCodeInvalid:
            error = msg.addUserToClassError_invitationCodeInvalid
            break

          case AddUserToClassError.MaxNumberOfStudentsPerClassReached:
            error = msg.addUsertoClassError_maxStudents
            break

          case AddUserToClassError.UserNotFound:
            error = msg.addUserToClassError_usernameNotFound
            break

          default:
            error = msg.serverError_generic
            break
        }

        if (error) {
          setState((prev) => ({
            ...prev,
            joinLoading: false,
            joinError: error,
          }))
        } else {
          location.reload()
        }
      })
      .catch(() => {
        setState((prev) => ({
          ...prev,
          joinLoading: false,
          joinError: msg.serverError_generic,
        }))
      })
  }

  return (
    <div>
      <EmailUndeliverableAlert />
      <FormContainer>
        <EditableField
          id="profile_fullname"
          value={user.name}
          onChange={(name) => handleProfileChanged({ name })}
          onClearValidation={() => handleClearValidation("name")}
          validationError={profilePage.validationErrors.name}
          disabled={state.isLoading}
        />
        <ImageUploader
          src={user.profilePictureUrl}
          alt={msg.profileImage}
          request={handleProfilePictureChanged}
          maxSize={{ width: 128, height: 128 }}
        />
        <EditableField
          id="profile_email"
          label={msg.profileEmail}
          value={user.email || ""}
          onChange={(email) => handleProfileChanged({ email })}
          onClearValidation={() => handleClearValidation("email")}
          validationError={profilePage.validationErrors.email}
          disabled={state.isLoading}
        />
        {/*<EditableField*/}
        {/*  id="profile_city"*/}
        {/*  value={user.city || msg.profileCity}*/}
        {/*  onChange={city => handleProfileChanged({city}) }*/}
        {/*  onClearValidation={() => handleClearValidation('city')}*/}
        {/*  validationError={profile.validationErrors.city}*/}
        {/*  disabled={profile.isLoading}*/}
        {/*/>*/}
      </FormContainer>

      <FlexContainer vertical centerHorizontally>
        <hr className="divider" />
        <h2>{msg.pageTitleClasses}</h2>
        {profilePage.classes.map((studentClass, studentClassIndex) => (
          <Fragment key={studentClass.classId}>
            {studentClassIndex > 0 && <hr className="small" />}

            <div
              key={studentClass.classId}
              className="student-class"
              title={studentClass.invitationCode}
            >
              {studentClass.name}
            </div>
          </Fragment>
        ))}

        {state.isInvitationExpanded ? (
          <FlexContainer className="gap-2 m-3" vertical>
            <TextInputGroup
              id={"profile_add_class_code"}
              value={state.invitationCode || ""}
              placeholder={msg.classesInvitationCodeLabel}
              onChange={(newCode) => {
                setState((prev) => ({ ...prev, invitationCode: newCode }))
              }}
              validationError={state.joinError}
              disabled={state.joinLoading}
            />

            <button
              className="btn btn-primary btn-sm"
              onClick={handleJoinClassRequested}
              disabled={state.joinLoading}
            >
              {msg.profileJoinClass}
            </button>
          </FlexContainer>
        ) : (
          <button
            className="btn btn-primary m-2 btn-round"
            onClick={() => {
              setState((prev) => ({ ...prev, isInvitationExpanded: true }))
            }}
          >
            +
          </button>
        )}
      </FlexContainer>
    </div>
  )
}
